import { CarrierLogo } from "@/components/common/CarrierLogo";
import { RateSourceBadge } from "@/features/rates/RateSourceBadge";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { cn } from "@/lib/utils";

import {
  entityTagKindDef,
  portRoleBadgeLabel,
  type KaiEntityTag,
} from "./kaiEntityTags";

/** Leading icon for an entity-tag row in the Kai `@` autosuggest menu. */
export function KaiEntityTagOptionLead({
  tag,
  className,
}: {
  tag: KaiEntityTag;
  className?: string;
}) {
  const { preferences } = useUserPreferences();
  const logoCarrier =
    tag.kind === "carrier"
      ? tag.value
      : tag.kind === "contract_ref"
        ? tag.metadata?.carrier
        : undefined;
  if (logoCarrier && preferences.showCarrierLogos) {
    return (
      <CarrierLogo
        carrier={logoCarrier}
        size={16}
        logoOnly
        className={cn("shrink-0", className)}
      />
    );
  }
  const Icon = entityTagKindDef(tag.kind).icon;
  return (
    <Icon className={cn("size-4 shrink-0 text-muted-foreground", className)} />
  );
}

/**
 * Trailing badge for an entity-tag menu row: the prepaid/collect/spot family
 * for a contract tag, or the lane-side chip (POL / POD / Port) for a port tag.
 * Returns `null` for the other kinds.
 */
export function KaiEntityTagOptionBadge({ tag }: { tag: KaiEntityTag }) {
  if (tag.kind === "contract_ref" && tag.metadata?.source_type) {
    return (
      <RateSourceBadge
        source={tag.metadata.source_type}
        className="shrink-0 text-[10px]"
      />
    );
  }
  if (tag.kind === "port") {
    return (
      <span className="shrink-0 rounded-md bg-cyan-500/15 px-1.5 py-px text-[10px] font-medium text-cyan-700 dark:text-cyan-300">
        {portRoleBadgeLabel(tag.metadata?.port_role)}
      </span>
    );
  }
  return null;
}

/** Inline `@tag` body for rendered user messages (logo + label when enabled). */
export function KaiEntityTagInlineBody({ tag }: { tag: KaiEntityTag }) {
  const { preferences } = useUserPreferences();
  const logoCarrier =
    tag.kind === "carrier"
      ? tag.value
      : tag.kind === "contract_ref"
        ? tag.metadata?.carrier
        : undefined;
  const showLogo = Boolean(logoCarrier) && preferences.showCarrierLogos;

  return (
    <>
      @
      {showLogo ? (
        <CarrierLogo
          carrier={logoCarrier!}
          size={14}
          logoOnly
          className="mx-0.5 shrink-0"
        />
      ) : null}
      {tag.label}
    </>
  );
}
